/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import TransactionResponse from '../../../domain/context/transaction/TransactionResponse.js';
import { ContractId as HContractId } from '@hashgraph/sdk';
import { HederaReserve__factory, HederaTokenManager__factory, IHederaTokenService__factory, ProxyAdmin__factory, StableCoinFactory__factory, StableCoinProxyAdmin__factory, IHTSToken__factory, } from '@hashgraph/stablecoin-npm-contracts';
import TransactionAdapter from '../TransactionAdapter';
import { ethers, Signer } from 'ethers';
import { singleton } from 'tsyringe';
import BigDecimal from '../../../domain/context/shared/BigDecimal.js';
import Injectable from '../../../core/Injectable.js';
import { CapabilityDecider, Decision } from '../CapabilityDecider.js';
import { Operation } from '../../../domain/context/stablecoin/Capability.js';
import { CapabilityError } from '../hs/error/CapabilityError.js';
import { StableCoinRole } from '../../../domain/context/stablecoin/StableCoinRole.js';
import detectEthereumProvider from '@metamask/detect-provider';
import { RuntimeError } from '../../../core/error/RuntimeError.js';
import Account from '../../../domain/context/account/Account.js';
import { HederaId } from '../../../domain/context/shared/HederaId.js';
import { lazyInject } from '../../../core/decorator/LazyInjectDecorator.js';
import { MirrorNodeAdapter } from '../mirror/MirrorNodeAdapter.js';
import NetworkService from '../../../app/service/NetworkService.js';
import { TokenSupplyType } from '../../../domain/context/stablecoin/TokenSupply.js';
import { FactoryStableCoin } from '../../../domain/context/factory/FactoryStableCoin.js';
import { ACCEPT_PROXY_OWNER_GAS, BURN_GAS, CASHIN_GAS, CHANGE_PROXY_OWNER_GAS, CREATE_SC_GAS, DECREASE_SUPPLY_GAS, DELETE_GAS, FREEZE_GAS, GRANT_KYC_GAS, GRANT_ROLES_GAS, INCREASE_SUPPLY_GAS, MAX_ROLES_GAS, PAUSE_GAS, RESCUE_GAS, RESCUE_HBAR_GAS, RESET_SUPPLY_GAS, REVOKE_KYC_GAS, REVOKE_ROLES_GAS, TOKEN_CREATION_COST_HBAR, UNFREEZE_GAS, UNPAUSE_GAS, UPDATE_PROXY_IMPLEMENTATION_GAS, UPDATE_RESERVE_ADDRESS_GAS, UPDATE_RESERVE_AMOUNT_GAS, UPDATE_TOKEN_GAS, WIPE_GAS, ASSOCIATE_GAS, } from '../../../core/Constants.js';
import { WalletConnectError } from '../../../domain/context/network/error/WalletConnectError.js';
import EventService from '../../../app/service/event/EventService.js';
import { ConnectionState, WalletEvents, } from '../../../app/service/event/WalletEvent.js';
import { SupportedWallets } from '../../../domain/context/network/Wallet.js';
import { RPCTransactionResponseAdapter } from './RPCTransactionResponseAdapter.js';
import LogService from '../../../app/service/LogService.js';
import { WalletConnectRejectedError } from '../../../domain/context/network/error/WalletConnectRejectedError.js';
import { TransactionResponseError } from '../error/TransactionResponseError.js';
import { SigningError } from '../hs/error/SigningError.js';
import { RESERVE_DECIMALS } from '../../../domain/context/reserve/Reserve.js';
import { FactoryRole } from '../../../domain/context/factory/FactoryRole.js';
import { HederaNetworks, unrecognized, } from '../../../domain/context/network/Environment.js';
import { CommandBus } from '../../../core/command/CommandBus.js';
import { SetNetworkCommand } from '../../../app/usecase/command/network/setNetwork/SetNetworkCommand.js';
import { SetConfigurationCommand } from '../../../app/usecase/command/network/setConfiguration/SetConfigurationCommand.js';
import Hex from '../../../core/Hex.js';
import { keccak256 } from 'ethereum-cryptography/keccak';
let RPCTransactionAdapter = class RPCTransactionAdapter extends TransactionAdapter {
    constructor(mirrorNodeAdapter, networkService, eventService, commandBus) {
        super();
        this.mirrorNodeAdapter = mirrorNodeAdapter;
        this.networkService = networkService;
        this.eventService = eventService;
        this.commandBus = commandBus;
        this.registerMetamaskEvents();
    }
    async create(coin, factory, hederaTokenManager, createReserve, reserveAddress, reserveInitialAmount, proxyAdminOwnerAccount) {
        try {
            const cashinRole = {
                account: coin.cashInRoleAccount == undefined ||
                    coin.cashInRoleAccount.toString() == '0.0.0'
                    ? '0x0000000000000000000000000000000000000000'
                    : await this.getEVMAddress(coin.cashInRoleAccount),
                allowance: coin.cashInRoleAllowance
                    ? coin.cashInRoleAllowance.toFixedNumber()
                    : BigDecimal.ZERO.toFixedNumber(),
            };
            const providedKeys = [
                coin.adminKey,
                coin.kycKey,
                coin.freezeKey,
                coin.wipeKey,
                coin.supplyKey,
                coin.feeScheduleKey,
                coin.pauseKey,
            ];
            const keys = this.setKeysForSmartContract(providedKeys);
            const providedRoles = [
                {
                    account: coin.burnRoleAccount,
                    role: StableCoinRole.BURN_ROLE,
                },
                {
                    account: coin.wipeRoleAccount,
                    role: StableCoinRole.WIPE_ROLE,
                },
                {
                    account: coin.rescueRoleAccount,
                    role: StableCoinRole.RESCUE_ROLE,
                },
                {
                    account: coin.pauseRoleAccount,
                    role: StableCoinRole.PAUSE_ROLE,
                },
                {
                    account: coin.freezeRoleAccount,
                    role: StableCoinRole.FREEZE_ROLE,
                },
                {
                    account: coin.deleteRoleAccount,
                    role: StableCoinRole.DELETE_ROLE,
                },
                { account: coin.kycRoleAccount, role: StableCoinRole.KYC_ROLE },
            ];
            const roles = await Promise.all(providedRoles
                .filter((item) => {
                return (item.account &&
                    item.account.value !== HederaId.NULL.value);
            })
                .map(async (item) => {
                const role = new FactoryRole();
                role.role = item.role;
                role.account = await this.getEVMAddress(item.account);
                return role;
            }));
            const stableCoinToCreate = new FactoryStableCoin(coin.name, coin.symbol, coin.freezeDefault ?? false, coin.supplyType == TokenSupplyType.FINITE, coin.maxSupply
                ? coin.maxSupply.toFixedNumber()
                : BigDecimal.ZERO.toFixedNumber(), coin.initialSupply
                ? coin.initialSupply.toFixedNumber()
                : BigDecimal.ZERO.toFixedNumber(), coin.decimals, reserveAddress == undefined ||
                reserveAddress.toString() == '0.0.0'
                ? '0x0000000000000000000000000000000000000000'
                : HContractId.fromString((await this.mirrorNodeAdapter.getContractInfo(reserveAddress.value)).evmAddress).toString(), reserveInitialAmount
                ? reserveInitialAmount.toFixedNumber()
                : BigDecimal.ZERO.toFixedNumber(), createReserve, keys, roles, cashinRole, coin.metadata ?? '', proxyAdminOwnerAccount == undefined ||
                proxyAdminOwnerAccount.toString() == '0.0.0'
                ? '0x0000000000000000000000000000000000000000'
                : HContractId.fromString(proxyAdminOwnerAccount.value).toSolidityAddress());
            const factoryInstance = StableCoinFactory__factory.connect((await this.mirrorNodeAdapter.getContractInfo(factory.value))
                .evmAddress, this.signerOrProvider);
            LogService.logTrace('Deploying factory: ', {
                tokenManager: hederaTokenManager.value,
                stableCoin: stableCoinToCreate,
            });
            const res = await factoryInstance.deployStableCoin(stableCoinToCreate, (await this.mirrorNodeAdapter.getContractInfo(hederaTokenManager.value)).evmAddress, {
                value: ethers.utils.parseEther(TOKEN_CREATION_COST_HBAR.toString()),
                gasLimit: CREATE_SC_GAS,
            });
            // Put it into an array since structs change the response from the event and its not a simple array
            return await RPCTransactionResponseAdapter.manageResponse(res, this.networkService.environment, 'Deployed');
        }
        catch (error) {
            LogService.logError(error);
            throw new SigningError(`Unexpected error in RPCTransactionAdapter create operation : ${error}`);
        }
    }
    async init(debug = false) {
        !debug && (await this.connectMetamask(false));
        const eventData = {
            initData: {
                account: this.account,
                pairing: '',
                topic: '',
            },
            wallet: SupportedWallets.METAMASK,
        };
        this.eventService.emit(WalletEvents.walletInit, eventData);
        LogService.logTrace('Metamask Initialized ', eventData);
        return this.networkService.environment;
    }
    setMirrorNodes(mirrorNodes) {
        if (mirrorNodes)
            this.mirrorNodes = mirrorNodes;
    }
    setJsonRpcRelays(jsonRpcRelays) {
        if (jsonRpcRelays)
            this.jsonRpcRelays = jsonRpcRelays;
    }
    setFactories(factories) {
        if (factories)
            this.factories = factories;
    }
    async register(account, debug = false) {
        if (account) {
            const accountMirror = await this.mirrorNodeAdapter.getAccountInfo(account.id);
            this.account = account;
            this.account.publicKey = accountMirror.publicKey;
        }
        Injectable.registerTransactionHandler(this);
        !debug && (await this.connectMetamask());
        LogService.logTrace('Metamask registered as handler');
        return Promise.resolve({ account });
    }
    stop() {
        this.eventService.emit(WalletEvents.walletConnectionStatusChanged, {
            status: ConnectionState.Disconnected,
            wallet: SupportedWallets.METAMASK,
        });
        LogService.logTrace('Metamask stopped');
        this.eventService.emit(WalletEvents.walletDisconnect, {
            wallet: SupportedWallets.METAMASK,
        });
        return Promise.resolve(true);
    }
    getMirrorNodeAdapter() {
        return this.mirrorNodeAdapter;
    }
    async update(coin, name, symbol, autoRenewPeriod, expirationTime, kycKey, freezeKey, feeScheduleKey, pauseKey, wipeKey, metadata) {
        const params = new Params({
            name: name,
            symbol: symbol,
            autoRenewPeriod: autoRenewPeriod,
            expirationTime: expirationTime,
            kycKey: kycKey,
            freezeKey: freezeKey,
            feeScheduleKey: feeScheduleKey,
            pauseKey: pauseKey,
            wipeKey: wipeKey,
            metadata: metadata,
        });
        return this.performOperation(coin, Operation.UPDATE, params);
    }
    async wipe(coin, targetId, amount) {
        const params = new Params({
            targetId: await this.getEVMAddress(targetId),
            amount: amount,
        });
        return this.performOperation(coin, Operation.WIPE, params);
    }
    async cashin(coin, targetId, amount) {
        const params = new Params({
            targetId: targetId.toString(),
            amount: amount,
        });
        return this.performOperation(coin, Operation.CASH_IN, params);
    }
    async burn(coin, amount) {
        const params = new Params({
            amount: amount,
        });
        return this.performOperation(coin, Operation.BURN, params);
    }
    async freeze(coin, targetId) {
        const params = new Params({
            targetId: await this.getEVMAddress(targetId),
        });
        return this.performOperation(coin, Operation.FREEZE, params);
    }
    async unfreeze(coin, targetId) {
        const params = new Params({
            targetId: await this.getEVMAddress(targetId),
        });
        return this.performOperation(coin, Operation.UNFREEZE, params);
    }
    async pause(coin) {
        return this.performOperation(coin, Operation.PAUSE);
    }
    async unpause(coin) {
        return this.performOperation(coin, Operation.UNPAUSE);
    }
    async rescue(coin, amount) {
        const params = new Params({
            amount: amount,
        });
        return this.performOperation(coin, Operation.RESCUE, params);
    }
    async rescueHBAR(coin, amount) {
        const params = new Params({
            amount: amount,
        });
        return this.performOperation(coin, Operation.RESCUE_HBAR, params);
    }
    async delete(coin) {
        return this.performOperation(coin, Operation.DELETE);
    }
    async getReserveAddress(coin) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            const res = await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).getReserveAddress();
            return new TransactionResponse(undefined, res.toString());
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter update reserve operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async updateReserveAddress(coin, reserveAddress) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).updateReserveAddress((await this.mirrorNodeAdapter.getContractInfo(reserveAddress.toHederaAddress().toString())).evmAddress, { gasLimit: UPDATE_RESERVE_ADDRESS_GAS }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter update reserve operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async getReserveAmount(coin) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            const res = await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).getReserveAmount();
            return new TransactionResponse(undefined, BigDecimal.fromStringFixed(res.toString(), RESERVE_DECIMALS));
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter update reserve operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async updateReserveAmount(reserveAddress, amount) {
        try {
            return RPCTransactionResponseAdapter.manageResponse(await HederaReserve__factory.connect((await this.mirrorNodeAdapter.getContractInfo(reserveAddress.toHederaAddress().toString())).evmAddress, this.signerOrProvider).setAmount(amount.toBigNumber(), {
                gasLimit: UPDATE_RESERVE_AMOUNT_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter updatePorAmount operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async upgradeImplementation(proxy, proxyAdminId, implementationId) {
        try {
            return RPCTransactionResponseAdapter.manageResponse(await ProxyAdmin__factory.connect((await this.mirrorNodeAdapter.getContractInfo(proxyAdminId.toHederaAddress().toString())).evmAddress, this.signerOrProvider).upgrade((await this.mirrorNodeAdapter.getContractInfo(proxy.toHederaAddress().toString())).evmAddress, (await this.mirrorNodeAdapter.getContractInfo(implementationId.toHederaAddress().toString())).evmAddress, {
                gasLimit: UPDATE_PROXY_IMPLEMENTATION_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter updatePorAmount operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async changeOwner(proxyAdminId, targetId) {
        try {
            return RPCTransactionResponseAdapter.manageResponse(await ProxyAdmin__factory.connect(proxyAdminId.toHederaAddress().toSolidityAddress(), this.signerOrProvider).transferOwnership(await this.getEVMAddress(targetId), {
                gasLimit: CHANGE_PROXY_OWNER_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter updatePorAmount operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async acceptOwner(proxyAdminId) {
        try {
            return RPCTransactionResponseAdapter.manageResponse(await StableCoinProxyAdmin__factory.connect(proxyAdminId.toHederaAddress().toSolidityAddress(), this.signerOrProvider).acceptOwnership({
                gasLimit: ACCEPT_PROXY_OWNER_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter updatePorAmount operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async grantRole(coin, targetId, role) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).grantRole(role, await this.getEVMAddress(targetId), {
                gasLimit: GRANT_ROLES_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter grantRole operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async revokeRole(coin, targetId, role) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).revokeRole(role, await this.getEVMAddress(targetId), {
                gasLimit: REVOKE_ROLES_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter revokeRole operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async grantRoles(coin, targetsId, roles, amounts) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            const amountsFormatted = [];
            amounts.forEach((amount) => {
                amountsFormatted.push(amount.toBigNumber());
            });
            const accounts = [];
            for (let i = 0; i < targetsId.length; i++) {
                accounts.push(
                // (await this.accountToEvmAddress(targetsId[i])).toString(),
                await this.getEVMAddress(targetsId[i]));
            }
            let gas = targetsId.length * roles.length * GRANT_ROLES_GAS;
            gas = gas > MAX_ROLES_GAS ? MAX_ROLES_GAS : gas;
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).grantRoles(roles, accounts, amountsFormatted, {
                gasLimit: gas,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter grantRoles operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async revokeRoles(coin, targetsId, roles) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            const accounts = [];
            for (let i = 0; i < targetsId.length; i++) {
                accounts.push(
                // (await this.accountToEvmAddress(targetsId[i])).toString(),
                await this.getEVMAddress(targetsId[i]));
            }
            let gas = targetsId.length * roles.length * REVOKE_ROLES_GAS;
            gas = gas > MAX_ROLES_GAS ? MAX_ROLES_GAS : gas;
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).revokeRoles(roles, accounts, {
                gasLimit: gas,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter revokeRoles operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async grantSupplierRole(coin, targetId, amount) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).grantSupplierRole(
            // (await this.accountToEvmAddress(targetId)).toString(),
            await this.getEVMAddress(targetId), amount.toBigNumber(), { gasLimit: GRANT_ROLES_GAS }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter grantSupplierRole operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async grantUnlimitedSupplierRole(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                    network: this.networkService.environment,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).grantUnlimitedSupplierRole(
            // (await this.accountToEvmAddress(targetId)).toString(),
            await this.getEVMAddress(targetId), { gasLimit: GRANT_ROLES_GAS }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter grantUnlimitedSupplierRole operation : ${error}`,
                network: this.networkService.environment,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async revokeSupplierRole(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                    network: this.networkService.environment,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).revokeSupplierRole(await this.getEVMAddress(targetId), {
                gasLimit: REVOKE_ROLES_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter revokeSupplierRole operation : ${error}`,
                network: this.networkService.environment,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async hasRole(coin, targetId, role) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                    network: this.networkService.environment,
                });
            const res = await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).hasRole(role, await this.getEVMAddress(targetId));
            return new TransactionResponse(undefined, res.valueOf());
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter hasRole operation : ${error}`,
                network: this.networkService.environment,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async balanceOf(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                    network: this.networkService.environment,
                });
            const res = await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).balanceOf(await this.getEVMAddress(targetId));
            return new TransactionResponse(undefined, BigDecimal.fromStringFixed(res.toString(), coin.coin.decimals));
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter balanceOf operation : ${error}`,
                network: this.networkService.environment,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async isUnlimitedSupplierAllowance(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                    network: this.networkService.environment,
                });
            return new TransactionResponse(undefined, await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).isUnlimitedSupplierAllowance(
            // (await this.accountToEvmAddress(targetId)).toString(),
            await this.getEVMAddress(targetId)));
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter isUnlimitedSupplierAllowance operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async supplierAllowance(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            const res = await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).getSupplierAllowance(
            // (await this.accountToEvmAddress(targetId)).toString(),
            await this.getEVMAddress(targetId));
            return new TransactionResponse(undefined, BigDecimal.fromStringFixed(res.toString(), coin.coin.decimals));
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter supplierAllowance operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async resetSupplierAllowance(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).resetSupplierAllowance(await this.getEVMAddress(targetId), {
                gasLimit: RESET_SUPPLY_GAS,
            }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter resetSupplierAllowance operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async increaseSupplierAllowance(coin, targetId, amount) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).increaseSupplierAllowance(
            // (await this.accountToEvmAddress(targetId)).toString(),
            await this.getEVMAddress(targetId), amount.toBigNumber(), { gasLimit: INCREASE_SUPPLY_GAS }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter increaseSupplierAllowance operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async decreaseSupplierAllowance(coin, targetId, amount) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).decreaseSupplierAllowance(
            // (await this.accountToEvmAddress(targetId)).toString(),
            await this.getEVMAddress(targetId), amount.toBigNumber(), { gasLimit: DECREASE_SUPPLY_GAS }), this.networkService.environment);
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter decreaseSupplierAllowance operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async getRoles(coin, targetId) {
        try {
            if (!coin.coin.evmProxyAddress?.toString())
                throw new TransactionResponseError({
                    network: this.networkService.environment,
                    RPC_relay: true,
                    message: `StableCoin ${coin.coin.name} does not have a proxy address`,
                });
            return new TransactionResponse(undefined, await HederaTokenManager__factory.connect(coin.coin.evmProxyAddress?.toString(), this.signerOrProvider).getRoles(await this.getEVMAddress(targetId)));
        }
        catch (error) {
            LogService.logError(error);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter getRoles operation : ${error}`,
                transactionId: error.error?.transactionId,
            });
        }
    }
    async grantKyc(coin, targetId) {
        const params = new Params({
            targetId: await this.getEVMAddress(targetId),
        });
        return this.performOperation(coin, Operation.GRANT_KYC, params);
    }
    async revokeKyc(coin, targetId) {
        const params = new Params({
            targetId: await this.getEVMAddress(targetId),
        });
        return this.performOperation(coin, Operation.REVOKE_KYC, params);
    }
    async associateToken(tokenId, targetId) {
        try {
            const HTSTokenEVMAddress = tokenId
                .toHederaAddress()
                .toSolidityAddress();
            const response = await RPCTransactionResponseAdapter.manageResponse(await IHTSToken__factory.connect(HTSTokenEVMAddress, this.signerOrProvider).associate({ gasLimit: ASSOCIATE_GAS }), this.networkService.environment);
            this.logTransaction(response.id ?? '', this.networkService.environment);
            return response;
        }
        catch (error) {
            LogService.logError(error);
            this.logTransaction(error.error.transactionHash ?? '', this.networkService.environment);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter association operation : ${error}`,
                transactionId: error.error.transactionHash,
            });
        }
    }
    async contractCall(contractAddress, functionName, param) {
        const tokenManager = IHederaTokenService__factory.connect(contractAddress, this.signerOrProvider).functions;
        return tokenManager[functionName](...param);
    }
    async signAndSendTransaction(t) {
        throw new RuntimeError('Method not implemented.');
    }
    getAccount() {
        return this.account;
    }
    /**
     * TODO consider leaving this as a service and putting two implementations on top for rpc and web wallet.
     */
    async connectMetamask(pair = true) {
        try {
            const ethProvider = await detectEthereumProvider({
                mustBeMetaMask: true,
                silent: true,
            });
            if (ethProvider) {
                this.eventService.emit(WalletEvents.walletFound, {
                    wallet: SupportedWallets.METAMASK,
                    name: SupportedWallets.METAMASK,
                });
                if (ethProvider.isMetaMask) {
                    if (pair && !ethereum.isConnected())
                        throw new WalletConnectError('Metamask is not connected!');
                    pair && (await this.pairWallet());
                    this.web3Provider = new ethers.providers.Web3Provider(
                    // @ts-expect-error No TS compatibility
                    ethereum);
                    this.signerOrProvider = this.web3Provider.getSigner();
                }
                else {
                    throw new WalletConnectError('Metamask was not found!');
                }
            }
        }
        catch (error) {
            if ('code' in error && error.code === 4001) {
                throw new WalletConnectRejectedError(SupportedWallets.METAMASK);
            }
            if (error instanceof WalletConnectError) {
                throw error;
            }
            throw new RuntimeError(error.message);
        }
    }
    async setMetasmaskAccount(evmAddress) {
        let mirrorAccount = undefined;
        try {
            mirrorAccount = await this.mirrorNodeAdapter.getAccountInfo(evmAddress);
        }
        catch (e) {
            LogService.logError('account could not be retrieved from mirror error : ' + e);
        }
        if (mirrorAccount) {
            this.account = new Account({
                id: mirrorAccount.id,
                evmAddress: mirrorAccount.accountEvmAddress,
                publicKey: mirrorAccount.publicKey,
            });
            this.web3Provider = new ethers.providers.Web3Provider(
            // @ts-expect-error No TS compatibility
            ethereum);
            this.signerOrProvider = this.web3Provider.getSigner();
        }
        else {
            this.account = Account.NULL;
        }
        LogService.logTrace('Paired Metamask Wallet Event:', this.account);
    }
    async setMetamaskNetwork(chainId) {
        let network = unrecognized;
        let factoryId = '';
        let mirrorNode = {
            baseUrl: '',
            apiKey: '',
            headerName: '',
        };
        let rpcNode = {
            baseUrl: '',
            apiKey: '',
            headerName: '',
        };
        const metamaskNetwork = HederaNetworks.find((i) => '0x' + i.chainId.toString(16) === chainId.toString());
        if (metamaskNetwork) {
            network = metamaskNetwork.network;
            if (this.factories) {
                try {
                    const result = this.factories.factories.find((i) => i.environment === metamaskNetwork.network);
                    if (result) {
                        factoryId = result.factory.toString();
                    }
                }
                catch (e) {
                    console.error(`Factories could not be found for environment ${metamaskNetwork.network} in  the initially provided list`);
                }
            }
            if (this.mirrorNodes) {
                try {
                    const result = this.mirrorNodes.nodes.find((i) => i.environment === metamaskNetwork.network);
                    if (result) {
                        mirrorNode = result.mirrorNode;
                    }
                }
                catch (e) {
                    console.error(`Mirror Nodes could not be found for environment ${metamaskNetwork.network} in  the initially provided list`);
                }
            }
            if ((this, this.jsonRpcRelays)) {
                try {
                    const result = this.jsonRpcRelays.nodes.find((i) => i.environment === metamaskNetwork.network);
                    if (result) {
                        rpcNode = result.jsonRpcRelay;
                    }
                }
                catch (e) {
                    console.error(`RPC Nodes could not be found for environment ${metamaskNetwork.network} in  the initially provided list`);
                }
            }
            LogService.logTrace('Metamask Network:', chainId);
        }
        else {
            console.error(chainId + ' not an hedera network');
        }
        await this.commandBus.execute(new SetNetworkCommand(network, mirrorNode, rpcNode));
        await this.commandBus.execute(new SetConfigurationCommand(factoryId));
        this.web3Provider = new ethers.providers.Web3Provider(
        // @ts-expect-error No TS compatibility
        ethereum);
        this.signerOrProvider = this.web3Provider.getSigner();
        // await new Promise(f => setTimeout(f, 3000));
    }
    async pairWallet() {
        const accts = await ethereum.request({
            method: 'eth_requestAccounts',
        });
        if (accts && 'length' in accts) {
            const evmAddress = accts[0];
            const chainId = await ethereum.request({ method: 'eth_chainId' });
            await this.setMetamaskNetwork(chainId);
            await this.setMetasmaskAccount(evmAddress);
            this.eventService.emit(WalletEvents.walletPaired, {
                data: {
                    account: this.account,
                    pairing: '',
                    topic: '',
                },
                network: {
                    name: this.networkService.environment,
                    recognized: this.networkService.environment != unrecognized,
                    factoryId: this.networkService.configuration
                        ? this.networkService.configuration.factoryAddress
                        : '',
                },
                wallet: SupportedWallets.METAMASK,
            });
        }
        else {
            LogService.logTrace('Paired Metamask failed with no accounts');
            this.eventService.emit(WalletEvents.walletDisconnect, {
                wallet: SupportedWallets.METAMASK,
            });
        }
    }
    registerMetamaskEvents() {
        try {
            if (typeof window === 'undefined' || !window?.ethereum)
                return;
            ethereum.on('accountsChanged', async (acct) => {
                const accounts = acct;
                if (accounts.length > 0 &&
                    this.account &&
                    accounts[0] !== this.account.evmAddress) {
                    await this.setMetasmaskAccount(accounts[0]);
                    this.eventService.emit(WalletEvents.walletPaired, {
                        data: {
                            account: this.account,
                            pairing: '',
                            topic: '',
                        },
                        network: {
                            name: this.networkService.environment,
                            recognized: this.networkService.environment != unrecognized,
                            factoryId: this.networkService.configuration
                                .factoryAddress,
                        },
                        wallet: SupportedWallets.METAMASK,
                    });
                }
                else {
                    LogService.logTrace('Metamask disconnected from the wallet');
                    this.eventService.emit(WalletEvents.walletDisconnect, {
                        wallet: SupportedWallets.METAMASK,
                    });
                }
            });
            ethereum.on('chainChanged', async (chainId) => {
                await this.setMetamaskNetwork(chainId);
                let evmAddress = this.account.evmAddress;
                if (!evmAddress) {
                    const accts = await ethereum.request({
                        method: 'eth_requestAccounts',
                    });
                    evmAddress =
                        accts && 'length' in accts
                            ? accts[0]
                            : '';
                }
                await this.setMetasmaskAccount(evmAddress);
                this.eventService.emit(WalletEvents.walletPaired, {
                    data: {
                        account: this.account,
                    },
                    network: {
                        name: this.networkService.environment,
                        recognized: this.networkService.environment != unrecognized,
                        factoryId: this.networkService.configuration
                            ? this.networkService.configuration.factoryAddress
                            : '',
                    },
                    wallet: SupportedWallets.METAMASK,
                });
            });
        }
        catch (error) {
            LogService.logError(error);
            throw new WalletConnectError('Ethereum is not defined');
        }
    }
    async performOperation(coin, operation, params) {
        try {
            let response;
            switch (CapabilityDecider.decide(coin, operation)) {
                case Decision.CONTRACT:
                    if (!coin.coin.evmProxyAddress?.toString())
                        throw new Error(`StableCoin ${coin.coin.name} does not have a proxy address`);
                    response = await this.performSmartContractOperation(coin, operation, params);
                    this.logTransaction(response.id ?? '', this.networkService.environment);
                    return response;
                default:
                    const tokenId = coin.coin.tokenId
                        ? coin.coin.tokenId.value
                        : '';
                    const OperationNotAllowed = new CapabilityError(this.getAccount().id.value, operation, tokenId);
                    return new TransactionResponse(undefined, undefined, OperationNotAllowed);
            }
        }
        catch (error) {
            LogService.logError(error);
            this.logTransaction(error.error.transactionHash ?? '', this.networkService.environment);
            throw new TransactionResponseError({
                network: this.networkService.environment,
                RPC_relay: true,
                message: `Unexpected error in RPCTransactionAdapter ${operation} operation : ${error}`,
                transactionId: error.error.transactionHash,
            });
        }
    }
    async performSmartContractOperation(coin, operation, params) {
        const evmProxy = coin.coin.evmProxyAddress?.toString() ?? '';
        switch (operation) {
            case Operation.CASH_IN:
                const targetEvm = await this.getEVMAddress(HederaId.from(params.targetId));
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).mint(targetEvm.toString(), params.amount.toBigNumber(), { gasLimit: CASHIN_GAS }), this.networkService.environment);
            case Operation.BURN:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).burn(params.amount.toBigNumber(), {
                    gasLimit: BURN_GAS,
                }), this.networkService.environment);
            case Operation.WIPE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).wipe(params.targetId, params.amount.toBigNumber(), {
                    gasLimit: WIPE_GAS,
                }), this.networkService.environment);
            case Operation.RESCUE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).rescue(params.amount.toBigNumber(), {
                    gasLimit: RESCUE_GAS,
                }), this.networkService.environment);
            case Operation.RESCUE_HBAR:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).rescueHBAR(params.amount.toBigNumber(), {
                    gasLimit: RESCUE_HBAR_GAS,
                }), this.networkService.environment);
            case Operation.FREEZE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).freeze(params.targetId, { gasLimit: FREEZE_GAS }), this.networkService.environment);
            case Operation.UNFREEZE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).unfreeze(params.targetId, { gasLimit: UNFREEZE_GAS }), this.networkService.environment);
            case Operation.GRANT_KYC:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).grantKyc(params.targetId, { gasLimit: GRANT_KYC_GAS }), this.networkService.environment);
            case Operation.REVOKE_KYC:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).revokeKyc(params.targetId, {
                    gasLimit: REVOKE_KYC_GAS,
                }), this.networkService.environment);
            case Operation.PAUSE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).pause({ gasLimit: PAUSE_GAS }), this.networkService.environment);
            case Operation.UNPAUSE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).unpause({ gasLimit: UNPAUSE_GAS }), this.networkService.environment);
            case Operation.DELETE:
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).deleteToken({ gasLimit: DELETE_GAS }), this.networkService.environment);
            case Operation.UPDATE:
                const providedKeys = [
                    undefined,
                    params?.kycKey,
                    params?.freezeKey,
                    params?.wipeKey,
                    params?.supplyKey,
                    params?.feeScheduleKey,
                    params?.pauseKey,
                ];
                const filteredContractParams = {
                    tokenName: params?.name ?? '',
                    tokenSymbol: params?.symbol ?? '',
                    keys: this.setKeysForSmartContract(providedKeys),
                    second: params?.expirationTime
                        ? Math.floor(params.expirationTime / 1000000000)
                        : -1,
                    autoRenewPeriod: params?.autoRenewPeriod
                        ? params.autoRenewPeriod
                        : -1,
                    tokenMetadataURI: params?.metadata ?? '',
                };
                return RPCTransactionResponseAdapter.manageResponse(await HederaTokenManager__factory.connect(evmProxy, this.signerOrProvider).updateToken(filteredContractParams, {
                    gasLimit: UPDATE_TOKEN_GAS,
                }), this.networkService.environment);
            default:
                throw new Error(`Operation not implemented through Smart Contracts`);
        }
    }
    async sign(message) {
        if (!(this.signerOrProvider instanceof Signer))
            throw new Error('RPC instance is not a Signer.');
        const bytesToSign = Hex.toUint8Array(message);
        const bytesToSignHash = this.calcKeccak256(bytesToSign);
        const bytesToSignHashHex = '0x' + Hex.fromUint8Array(bytesToSignHash);
        const signature = await this.web3Provider.send('eth_sign', [
            this.account.evmAddress,
            bytesToSignHashHex,
        ]);
        return signature.toString().slice(0, 130);
    }
    calcKeccak256(message) {
        return Buffer.from(keccak256(message));
    }
};
RPCTransactionAdapter = __decorate([
    singleton(),
    __param(0, lazyInject(MirrorNodeAdapter)),
    __param(1, lazyInject(NetworkService)),
    __param(2, lazyInject(EventService)),
    __param(3, lazyInject(CommandBus)),
    __metadata("design:paramtypes", [MirrorNodeAdapter,
        NetworkService,
        EventService,
        CommandBus])
], RPCTransactionAdapter);
export default RPCTransactionAdapter;
class Params {
    constructor({ role, targetId, amount, name, symbol, autoRenewPeriod, expirationTime, kycKey, freezeKey, feeScheduleKey, pauseKey, wipeKey, supplyKey, metadata, }) {
        this.role = role;
        this.targetId = targetId;
        this.amount = amount;
        this.name = name;
        this.symbol = symbol;
        this.autoRenewPeriod = autoRenewPeriod;
        this.expirationTime = expirationTime;
        this.kycKey = kycKey;
        this.freezeKey = freezeKey;
        this.feeScheduleKey = feeScheduleKey;
        this.pauseKey = pauseKey;
        this.wipeKey = wipeKey;
        this.supplyKey = supplyKey;
        this.metadata = metadata;
    }
}
